import { clientWithApiClient } from "../../client";
import type { ApiClient } from "../../types";
import type {
  FleetBusinessProfile,
  UpdateBusinessProfileRequest,
} from "./types";

/**
 * Names the fleet the request is about. A member of several fleets is
 * answered for their first fleet when no fleet is named; API key callers
 * (the booking widget) name none, their key already belongs to one fleet.
 */
const withFleet = (path: string, fleetId?: string) =>
  fleetId ? `${path}?${new URLSearchParams({ fleet_id: fleetId })}` : path;

/**
 * Get fleet business profile
 * @param apiClient - API client configuration
 * @param fleetId - The fleet whose profile to read (omit for API key callers)
 * @returns Promise with business profile data
 */
export function getBusinessProfile(apiClient: ApiClient, fleetId?: string) {
  return clientWithApiClient<FleetBusinessProfile>(
    apiClient,
    withFleet("/fleet-business-profile", fleetId),
  );
}

/**
 * Update fleet business profile
 * @param apiClient - API client configuration
 * @param data - Business profile data to update
 * @param fleetId - The fleet whose profile to save
 * @returns Promise with updated business profile
 */
export function updateBusinessProfile(
  apiClient: ApiClient,
  data: UpdateBusinessProfileRequest,
  fleetId?: string,
) {
  return clientWithApiClient<FleetBusinessProfile>(
    apiClient,
    withFleet("/fleet-business-profile", fleetId),
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
}

/**
 * Upload logo for fleet business profile
 * @param apiClient - API client configuration
 * @param file - Logo file to upload
 * @param fleetId - The fleet whose logo to replace
 * @returns Promise with logo URL
 */
export async function uploadBusinessProfileLogo(
  apiClient: ApiClient,
  file: File,
  fleetId?: string,
): Promise<{ logo_url: string }> {
  const formData = new FormData();
  formData.append("logo", file);

  return clientWithApiClient<{ logo_url: string }>(
    apiClient,
    withFleet("/fleet-business-profile/logo", fleetId),
    {
      method: "POST",
      body: formData,
    },
  );
}
